import { Box, Chip, Menu, MenuItem, Typography } from "@mui/material";
import React, { useState } from "react";

import { monospaceStyle } from "../../theme";
import { FactSource } from "../../types/productPlane";
import { entityPath } from "../../utils/entityAddress";
import { usePrinting } from "../../utils/export/ExportStage";

export interface SourcesChipProps {
  // The documents behind one fact or one card
  sources: FactSource[];
}

// A source the list does not hold comes last
const byNumber = (left: FactSource, right: FactSource): number =>
  (left.number ?? Infinity) - (right.number ?? Infinity);

// Two lines of a passage at most; the rest is cut with an ellipsis
const clamp = (lines: number) =>
  ({
    display: "-webkit-box",
    WebkitLineClamp: lines,
    WebkitBoxOrient: "vertical",
    overflow: "hidden",
  }) as const;

/**
 * The sources behind a fact as one chip, "3 sources", where marks side by
 * side would not fit. Clicked, it opens the list of them: the number, the
 * title, the site and date, and what the source states or the quoted
 * passage. An entry clicked opens the source's profile in a new tab.
 * Renders nothing for a fact without a source, nor on a printed page.
 */
const SourcesChip: React.FC<SourcesChipProps> = ({ sources }) => {
  const printing = usePrinting();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);

  if (sources.length === 0 || printing) {
    return null;
  }
  // The sources of several facts name a document once
  const sorted = Array.from(
    new Map(sources.map((source) => [source.document_id, source])).values(),
  ).sort(byNumber);
  const label = `${sorted.length} ${sorted.length === 1 ? "source" : "sources"}`;

  return (
    <>
      <Chip
        label={label}
        size="small"
        variant="outlined"
        aria-haspopup="menu"
        aria-expanded={anchor !== null}
        onClick={(event) => {
          // A chip sits inside cards that may be clickable themselves
          event.stopPropagation();
          setAnchor(event.currentTarget);
        }}
        sx={{ height: 22, fontSize: "0.75rem", color: "link.main" }}
      />
      <Menu
        anchorEl={anchor}
        open={anchor !== null}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{ paper: { sx: { maxWidth: 440, maxHeight: 400 } } }}
      >
        {sorted.map((source) => {
          const origin = [source.site, source.date].filter(Boolean).join(" · ");
          const passage = source.quote
            ? `“${source.quote}”`
            : source.statement;
          return (
            <MenuItem
              key={`${source.document_id}-${source.number}`}
              component="a"
              href={entityPath(source.document_id, "source")}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setAnchor(null)}
              sx={{
                alignItems: "flex-start",
                gap: 1.25,
                whiteSpace: "normal",
                py: 1,
              }}
            >
              <Box
                component="span"
                sx={{ ...monospaceStyle, color: "link.main", fontSize: "0.8rem", pt: "2px" }}
              >
                [{source.number ?? "•"}]
              </Box>
              <Box sx={{ minWidth: 0, display: "flex", flexDirection: "column", gap: 0.25 }}>
                <Typography variant="body2" sx={clamp(2)}>
                  {source.title ?? "Untitled document"}
                </Typography>
                {origin && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={monospaceStyle}
                  >
                    {origin}
                  </Typography>
                )}
                {passage && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ ...clamp(2), fontStyle: source.quote ? "italic" : "normal" }}
                  >
                    {passage}
                  </Typography>
                )}
              </Box>
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
};

export default SourcesChip;
